/** Twilio message scheduling constraints and utilities */

export const SCHEDULE_MIN_MINUTES = 15;
export const SCHEDULE_MAX_DAYS = 35;

/** Earliest allowed schedule time (15 min from now) */
export function getMinScheduleDate(): Date {
  return new Date(Date.now() + SCHEDULE_MIN_MINUTES * 60 * 1000);
}

/** Latest allowed schedule time (35 days from now) */
export function getMaxScheduleDate(): Date {
  return new Date(Date.now() + SCHEDULE_MAX_DAYS * 24 * 60 * 60 * 1000);
}

/** Check if a date falls within the valid scheduling window (15 min – 35 days) */
export function isValidScheduleDate(date: Date): boolean {
  const ts = date.getTime();
  const min = Date.now() + SCHEDULE_MIN_MINUTES * 60 * 1000;
  const max = Date.now() + SCHEDULE_MAX_DAYS * 24 * 60 * 60 * 1000;
  return ts >= min && ts <= max;
}

/** Format a schedule date for display — e.g. "Feb 18, 2:30 PM" */
export function formatScheduleDate(date: Date): string {
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
}

export interface SchedulePreset {
  label: string;
  getDate: () => Date;
}

/** Quick schedule presets */
export function getSchedulePresets(): SchedulePreset[] {
  return [
    {
      label: 'In 1 hour',
      getDate: () => new Date(Date.now() + 60 * 60 * 1000),
    },
    {
      label: 'In 2 hours',
      getDate: () => new Date(Date.now() + 2 * 60 * 60 * 1000),
    },
    {
      label: 'Tomorrow 9 AM',
      getDate: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
    {
      label: 'Tomorrow 2 PM',
      getDate: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(14, 0, 0, 0);
        return d;
      },
    },
  ];
}

/** Convert a Date to yyyy-MM-dd string for native date input */
export function toDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Convert a Date to HH:mm string for native time input */
export function toTimeInputValue(date: Date): string {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}
